import { Routes } from '@angular/router';
import { authGuard, guestGuard } from './core/guards/auth.guard';
import { roleGuard } from './core/guards/role.guard';
import { statementSendGuard } from './features/statements/send/statement-send-guard';
import { readingBatchGuard } from './features/utilities/readings/batch/reading-batch.guard';

export const routes: Routes = [
  {
    path: 'login',
    canActivate: [guestGuard],
    loadComponent: () =>
      import('./features/auth/login.component').then((m) => m.LoginComponent),
  },
  {
    path: 'auth/set-password',
    loadComponent: () =>
      import('./features/auth/set-password.component').then((m) => m.SetPasswordComponent),
  },
  {
    path: 'auth/forgot-password',
    loadComponent: () =>
      import('./features/auth/forgot-password.component').then((m) => m.ForgotPasswordComponent),
  },
  {
    path: '',
    canActivate: [authGuard, roleGuard('admin', 'operator')],
    loadComponent: () =>
      import('./shared/components/layout/app-layout.component').then(
        (m) => m.AppLayoutComponent
      ),
    children: [
      {
        path: 'dashboard',
        loadComponent: () =>
          import('./features/dashboard/dashboard.component').then(
            (m) => m.DashboardComponent
          ),
      },
      {
        path: 'apartments',
        loadComponent: () =>
          import('./features/apartments/apartments.component').then(
            (m) => m.ApartmentsComponent
          ),
      },
      {
        path: 'apartments/:id',
        loadComponent: () =>
          import('./features/apartments/apartment-detail.component').then(
            (m) => m.ApartmentDetailComponent
          ),
      },
      {
        path: 'owners',
        loadComponent: () =>
          import('./features/owners/owners.component').then(
            (m) => m.OwnersComponent
          ),
      },
      {
        path: 'owners/:id',
        loadComponent: () =>
          import('./features/owners/owner-detail.component').then(
            (m) => m.OwnerDetailComponent
          ),
      },
      {
        path: 'charges',
        loadComponent: () =>
          import('./features/charges/charges.component').then(
            (m) => m.ChargesComponent
          ),
      },
      {
        path: 'payments',
        loadComponent: () =>
          import('./features/payments/payments.component').then(
            (m) => m.PaymentsComponent
          ),
      },
      {
        path: 'statements',
        canActivate: [roleGuard('admin', 'operator')],
        canDeactivate: [statementSendGuard],
        loadComponent: () =>
          import('./features/statements/statements.component').then(
            (m) => m.StatementsComponent
          ),
      },
      {
        path: 'statements/bulk',
        canActivate: [roleGuard('admin', 'operator')],
        canDeactivate: [statementSendGuard],
        loadComponent: () =>
          import('./features/statements/bulk/bulk-statements.component').then(
            (m) => m.BulkStatementsComponent
          ),
      },
      {
        path: 'statements/:id',
        canActivate: [roleGuard('admin', 'operator')],
        canDeactivate: [statementSendGuard],
        loadComponent: () =>
          import('./features/statements/statement-detail.component').then(
            (m) => m.StatementDetailComponent
          ),
      },
      {
        path: 'utilities/prices',
        canActivate: [roleGuard('admin')],
        loadComponent: () =>
          import('./features/utilities/prices/utility-prices.component').then(
            (m) => m.UtilityPricesComponent,
          ),
      },
      {
        path: 'utilities/readings',
        canActivate: [roleGuard('admin', 'operator')],
        loadComponent: () =>
          import('./features/utilities/readings/utility-readings.component').then(
            (m) => m.UtilityReadingsComponent,
          ),
      },
      {
        path: 'utilities/readings/new',
        canActivate: [roleGuard('admin', 'operator')],
        loadComponent: () =>
          import('./features/utilities/readings/utility-reading-create.component').then(
            (m) => m.UtilityReadingCreateComponent,
          ),
      },
      {
        path: 'utilities/readings/batch',
        canActivate: [roleGuard('admin', 'operator')],
        canDeactivate: [readingBatchGuard],
        loadComponent: () =>
          import('./features/utilities/readings/batch/utility-reading-batch.component').then(
            (m) => m.UtilityReadingBatchComponent,
          ),
      },
      {
        path: 'utilities/readings/:id',
        canActivate: [roleGuard('admin', 'operator')],
        loadComponent: () =>
          import('./features/utilities/readings/utility-reading-detail.component').then(
            (m) => m.UtilityReadingDetailComponent,
          ),
      },
      {
        path: 'reports',
        redirectTo: 'reports/arrears',
        pathMatch: 'full',
      },
      {
        path: 'reports/arrears',
        loadComponent: () =>
          import('./features/reports/arrears/arrears-report.component').then(
            (m) => m.ArrearsReportComponent
          ),
      },
      {
        path: 'reports/ledger',
        loadComponent: () =>
          import('./features/reports/ledger/ledger-report.component').then(
            (m) => m.LedgerReportComponent
          ),
      },
      {
        path: 'settings',
        redirectTo: 'settings/annual-rates',
        pathMatch: 'full',
      },
      {
        path: 'settings/annual-rates',
        canActivate: [roleGuard('admin')],
        loadComponent: () =>
          import('./features/settings/annual-rates.component').then(
            (m) => m.AnnualRatesComponent
          ),
      },
      {
        path: 'settings/deposit',
        canActivate: [roleGuard('admin')],
        loadComponent: () =>
          import('./features/settings/deposit-settings.component').then(
            (m) => m.DepositSettingsComponent
          ),
      },
      {
        path: 'settings/currency-trail',
        canActivate: [roleGuard('admin')],
        loadComponent: () =>
          import('./features/settings/currency-trail-settings.component').then(
            (m) => m.CurrencyTrailSettingsComponent
          ),
      },
      {
        path: 'settings/users',
        canActivate: [roleGuard('admin')],
        loadComponent: () =>
          import('./features/settings/user-management.component').then(
            (m) => m.UserManagementComponent
          ),
      },
      { path: '', redirectTo: 'dashboard', pathMatch: 'full' },
    ],
  },
  {
    path: 'portal',
    loadChildren: () => import('./features/portal/portal.routes').then(m => m.portalRoutes),
  },
  { path: '**', redirectTo: '' },
];
